<script setup>
  // 메인 페이지 (B안 시안 퍼블 반영) - 헤더/푸터는 BaseLayout 공통 사용
  import { ref, computed, watch, onMounted, onBeforeUnmount } from "vue";
  import { useRouter } from "vue-router";
  import api, {
    publicApi,
    getAccessToken,
    getUserInfo,
    logout as apiLogout,
  } from "@/lib/api";
  import { useRoleStore } from "@/stores/role";
  import { useWorkspaceStore } from "@/stores/workspace";
  import { popupCheck } from "@/utils/popupGuide";
  import {
    clearSurveyPopupClosed,
    hideSurveyPopupToday,
    isSurveyPopupClosed,
    isSurveyPopupHiddenToday,
    setSurveyPopupClosed,
  } from "@/utils/surveyPopup";

  import PrivacyTeaModal from "@/components/PrivacyTeaModal.vue";
  import NewPrivacyModal from "@/components/newPrivacyModal.vue";
  import PrivacyStuModal from "@/components/PrivacyStuModal.vue";
  import NoticeModal from "@/components/NoticeModal.vue";
  import SurveyPopupModal from "@/components/SurveyPopupModal.vue";

  import heroDecoImg from "@/assets/images/main/mainb_hero_deco.png";
  import heroStdImg from "@/assets/images/main/mainb_hero_std.png";
  import cardSplanImg from "@/assets/images/main/mainb_card_splan.png";
  import cardAidlImg from "@/assets/images/main/mainb_card_aidl.png";
  import cardEssayImg from "@/assets/images/main/mainb_card_essay.png";

  const router = useRouter();
  const roleStore = useRoleStore();

  // ---------- 로그인 상태 ----------
  const isLoggedIn = ref(false);
  const userInfo = ref({});

  // 개인정보 동의 모달 상태
  const isOpenPrivacyTeaModal = ref(false);
  const isOpenPrivacyStuModal = ref(false);
  const newPrivacyModal = ref(false);

  const checkLoginStatus = () => {
    const token = getAccessToken();
    isLoggedIn.value = !!token;
    userInfo.value = token ? getUserInfo() : {};

    if (
      isLoggedIn.value &&
      userInfo.value?.isPrivacy &&
      userInfo.value?.role &&
      userInfo.value.isPrivacy !== "Y" &&
      userInfo.value.role !== "tea"
    ) {
      newPrivacyModal.value = true;
    }
  };

  // 개인정보 동의 모달 표시 여부 계산
  const shouldShowPrivacyModal = computed(() => {
    if (!isLoggedIn.value || !userInfo.value?.role) return false;
    return userInfo.value.isAgreedValid !== "Y";
  });

  const shouldShowTeacherModal = computed(() => {
    return (
      shouldShowPrivacyModal.value &&
      userInfo.value.role === "tea" &&
      userInfo.value.isConsentYn != "Y"
    );
  });

  const shouldShowStudentModal = computed(() => {
    return shouldShowPrivacyModal.value && userInfo.value.role === "stu";
  });

  watch(
    [shouldShowTeacherModal, shouldShowStudentModal],
    ([showTea, showStu]) => {
      isOpenPrivacyTeaModal.value = showTea;
      isOpenPrivacyStuModal.value = showStu;
    },
  );

  // roleStore.tempRole은 로그인/로그아웃 시 BaseHeader가 갱신하는 반응형 상태 —
  // DEV 로그인처럼 페이지 리로드 없이 로그인해도 이를 통해 상태가 반영된다
  watch(
    () => roleStore.tempRole,
    (role) => {
      checkLoginStatus();
      // 로그아웃되면 [닫기] 기록을 지워 다음 로그인 때 다시 띄운다
      if (!role) clearSurveyPopupClosed();
      fetchPopupSurveys();
    },
  );

  // 개인정보 동의 모달 취소 시 로그아웃 처리
  const handlePrivacyCancel = async () => {
    try {
      await apiLogout();
    } catch (error) {
      console.error("Logout failed:", error);
    }
  };

  const updatePrivacy = async () => {
    try {
      const { data } = await api.post("/account/updatePrivacy", {});
      if (data?.status === 200) {
        newPrivacyModal.value = false;

        const currentUserData = JSON.parse(
          localStorage.getItem("user") || "{}",
        );
        currentUserData.isPrivacy = "Y";
        localStorage.setItem("user", JSON.stringify(currentUserData));

        // 그 다음 userInfo 업데이트
        userInfo.value = getUserInfo();
      }
    } catch (error) {
      console.error("Failed to update privacy agreement:", error);
      alert("개인정보 동의 처리 중 오류가 발생했습니다. 다시 시도해 주세요.");
    }
  };

  // ---------- 공지 팝업 모달 ----------
  const showNoticeModal = ref(false);
  const noticeInfo = ref({});

  const getModalInfo = async () => {
    try {
      const { data } = await api.post("/notice/getNoticeInfo", {});

      if (data?.status === 200 && data?.result.noticeInfo) {
        const content = data.result.noticeInfo;

        if (content.is_popup) {
          if (content.target_grade != "all") {
            if (content.target_grade == userInfo.value.role) {
              showNoticeModal.value = true;
            } else {
              showNoticeModal.value = false;
            }
          } else {
            showNoticeModal.value = content.is_popup;
          }
        }

        noticeInfo.value = content;
      } else {
        showNoticeModal.value = false;
      }
    } catch (error) {
      console.error("Failed to fetch notice modal info:", error);
      showNoticeModal.value = false;
    }
  };

  // ---------- 대상 지정 설문 팝업 ----------
  // 관리자가 대상자로 지정한 교사에게만 노출. 개인정보 동의 → 공지 팝업이 모두 닫힌 뒤에 띄운다.
  // 제출/미동의 종료 전까지는 다시 로그인할 때마다 뜬다 (진행중이면 "이어서 참여").
  // [닫기] 는 이번 접속 동안만(로그인하면 초기화), [오늘 하루 보지 않기] 는 오늘 날짜까지 숨긴다.
  const surveyPopupList = ref([]);
  const isSurveyPopupDismissed = ref(isSurveyPopupClosed());

  const fetchPopupSurveys = async () => {
    surveyPopupList.value = [];
    isSurveyPopupDismissed.value = isSurveyPopupClosed();
    if (!isLoggedIn.value || userInfo.value?.role !== "tea" || isSurveyPopupHiddenToday()) {
      return;
    }
    try {
      const { data } = await api.post("/survey/tea/popupList", {});
      surveyPopupList.value = data?.success ? data.result?.list || [] : [];
    } catch (error) {
      console.error("설문 팝업 목록 조회 실패:", error);
      surveyPopupList.value = [];
    }
  };

  const showSurveyPopup = computed({
    get: () =>
      surveyPopupList.value.length > 0 &&
      !isSurveyPopupDismissed.value &&
      !isOpenPrivacyTeaModal.value &&
      !isOpenPrivacyStuModal.value &&
      !newPrivacyModal.value &&
      !showNoticeModal.value,
    set: (val) => {
      // [닫기] : 이번 접속 동안 숨김
      if (!val) {
        setSurveyPopupClosed(true);
        isSurveyPopupDismissed.value = true;
      }
    },
  });

  const hideSurveyPopupForToday = () => {
    hideSurveyPopupToday();
    isSurveyPopupDismissed.value = true;
  };

  // [참여하기] 는 숨김 기록을 남기지 않는다 (중간에 나와도 메인에서 다시 "이어서 참여" 안내)
  const joinSurvey = (survey) => {
    isSurveyPopupDismissed.value = true;
    router.push({ path: "/survey", query: { survey_id: survey.survey_id } });
  };

  // ---------- 이동/라우팅 ----------
  const goLogin = () => {
    window.location.href = process.env.VUE_APP_API_LOGIN_URL;
  };

  const goMyTest = () => {
    if (isLoggedIn.value) {
      router.push({ name: "MyTestGate" });
    } else {
      goLogin();
    }
  };

  // 응시하기: 교사는 시험 감독, 학생은 응시하기 화면으로 (비로그인은 로그인)
  // examType이 있으면 이동한 화면의 검사 유형 탭이 해당 유형으로 선택되도록 query로 전달한다.
  const goApplyExam = (examType) => {
    if (!isLoggedIn.value) {
      goLogin();
      return;
    }
    const query = EXAM_TYPE_ORDER.includes(examType) ? { examType } : {};
    if (roleStore.tempRole === "teacher") {
      router.push({ name: "MyTestTeacherWatch", query });
    } else if (roleStore.tempRole === "student") {
      router.push({ name: "MyTestStudentApply", query });
    } else {
      router.push({ name: "MyTestGate" });
    }
  };

  const moveNoticeList = () => router.push({ name: "NoticeList" });
  const moveNoticeDetail = (id) => router.push({ name: "NoticeDetail", params: { id } });
  // 1:1 문의는 로그인 필요 — 비로그인 시 로그인 화면으로
  // 학생은 이용 불가 — 안내 후 이동하지 않는다.
  const moveInquiry = () => {
    if (!isLoggedIn.value) {
      goLogin();
      return;
    }
    if (userInfo.value?.role === "stu" || roleStore.tempRole === "student") {
      alert("1:1 문의는 교사/관리자만 이용 가능합니다. 담당 선생님께 문의해 주세요.");
      return;
    }
    router.push({ name: "InquiryList" });
  };

  // ---------- 히어로 슬라이드 ----------
  // 문구는 admin '홈 화면 관리 > 배너 관리'(tb_potal_banner)에서 관리한다.
  // 조회 실패/빈 목록이면 아래 기본 문구를 그대로 노출한다(메인이 비어 보이면 안 되므로).
  const HERO_SLIDES_FALLBACK = [
    {
      title: "서울 학생 미래역량 진단 시스템",
      desc: "서울 학생의 잠재력과 미래 역량을 정확하게 진단하고 함양하기 위해 만들어진 서울특별시교육청의 진단검사 포털입니다.",
    },
    {
      title: "서울 학생 문해력·수리력 진단검사(S-PLAN)",
      desc: "미래역량 및 여러 교과를 학습하는데 기반이 되는 기초소양으로서의 문해력과 수리력을 진단하기 위해 개발한 검사 도구입니다.",
    },
    {
      title: "서울 학생 AI·디지털 리터러시 진단검사",
      desc: "서울 학생들의 AI·디지털 리터러시 수준을 정확히 진단하고, 맞춤형 지원 방안을 마련하고자 개발한 검사 도구입니다.",
    },
    {
      title: "AI·서논술형 평가 지원 시스템(채움AI)",
      desc: "교과별 수업 연계 서·논술형 평가 활성화를 위해 교사의 채점 업무 부담을 줄이고 학생 맞춤형 피드백을 제공하는 AI 평가 지원 도구입니다.",
    },
  ];

  const heroSlides = ref([...HERO_SLIDES_FALLBACK]);

  const activeSlide = ref(0);
  // 목록이 비동기로 교체되는 동안에도 템플릿이 undefined를 참조하지 않도록 방어
  const currentSlide = computed(
    () => heroSlides.value[activeSlide.value] ?? heroSlides.value[0] ?? { title: "", desc: "" },
  );

  let autoplayTimer = null;

  const goSlide = (idx) => {
    const total = heroSlides.value.length;
    if (!total) {
      activeSlide.value = 0;
      return;
    }
    activeSlide.value = ((idx % total) + total) % total;
  };
  const startAutoplay = () => {
    autoplayTimer = setInterval(() => goSlide(activeSlide.value + 1), 5000);
  };
  const resetAutoplay = () => {
    clearInterval(autoplayTimer);
    startAutoplay();
  };
  const onNavigate = (idx) => {
    goSlide(idx);
    resetAutoplay();
  };

  // 관리자가 textarea로 입력하므로 개행은 \n으로 저장되고,
  // .hero__title/.hero__desc가 white-space: pre-line이라 그대로 렌더링된다.
  // 다만 배너 관리 화면 도입 이전에 저장된 행에는 <br>이 문자열로 남아 있어 개행으로 되돌린다.
  const toPlainText = (value) => (value ?? "").replace(/<br\s*\/?>/gi, "\n");

  const fetchHeroSlides = async () => {
    try {
      const apiInstance = getAccessToken() ? api : publicApi;
      const { data } = await apiInstance.post("/comm/getBannerList", {});
      const list = Array.isArray(data?.result?.list) ? data.result.list : [];
      const slides = list.map((row) => ({
        title: toPlainText(row.title),
        desc: toPlainText(row.description),
      }));
      // 조회 결과가 없으면 기본 문구를 유지한다
      if (slides.length) heroSlides.value = slides;
    } catch (error) {
      console.error("Failed to load hero banners:", error);
    } finally {
      // 목록 길이가 바뀌면서 현재 인덱스가 범위를 벗어날 수 있으므로 보정
      if (activeSlide.value >= heroSlides.value.length) activeSlide.value = 0;
    }
  };

  // 다른 탭에서 동의했거나(storage), 토큰 재발급으로 유저정보가 갱신됐거나(auth:refreshed),
  // bfcache로 예전 화면이 복원된 경우(pageshow) 저장된 최신 동의 상태로 모달을 다시 판정한다.
  // (이미 동의했는데 열려 있던 모달에서 또 동의하는 중복 동의 방지)
  const handleUserStorageChange = (e) => {
    if (e.key === "user" || e.key === "accessToken") checkLoginStatus();
  };
  const handlePageShow = (e) => {
    if (e.persisted) checkLoginStatus();
  };

  onMounted(() => {
    startAutoplay();
    fetchHeroSlides();
    checkLoginStatus();
    fetchNoticeList();
    fetchExamSchedules();
    // 공지 팝업 여부가 정해진 뒤 설문 팝업을 조회해 두 팝업이 겹쳐 뜨지 않게 한다
    getModalInfo().finally(fetchPopupSurveys);
    window.addEventListener("storage", handleUserStorageChange);
    window.addEventListener("auth:refreshed", checkLoginStatus);
    window.addEventListener("pageshow", handlePageShow);
  });
  onBeforeUnmount(() => {
    clearInterval(autoplayTimer);
    window.removeEventListener("storage", handleUserStorageChange);
    window.removeEventListener("auth:refreshed", checkLoginStatus);
    window.removeEventListener("pageshow", handlePageShow);
  });

  // ---------- 서비스 카드 ----------
  // 체험하기: 기존 GuideListCard와 동일하게 외부 체험 페이지 팝업으로 연결
  const openGuideTutorial = (typeParam) => {
    popupCheck(`https://ubt.whalespace.io/web/tutorial?type=${typeParam}`);
  };

  const serviceCards = [
    {
      label: "S-PLAN",
      title: "서울 학생 문해력·수리력\n진단검사",
      cta: "체험하기",
      theme: "violet",
      img: cardSplanImg,
      examType: "MLSRD",
      onClick: () => openGuideTutorial("SENSPLAN-TRY"),
    },
    {
      label: "AI·디지털 리터러시",
      title: "서울 학생 AI·디지털 리터러시\n진단검사",
      cta: "체험하기",
      theme: "blue",
      img: cardAidlImg,
      examType: "LITERACY",
      // 리터러시는 UBT가 아닌 리터러시 전용 평가플랫폼에서 진행되므로 별도 팝업
      onClick: () => popupCheck("https://ai-literacy.sen.go.kr/practice/ready.do"),
    },
    {
      label: "채움AI",
      title: "AI·서논술형 평가\n채움AI",
      cta: "체험하기",
      theme: "teal",
      img: cardEssayImg,
      examType: "ESSAY",
      onClick: () => openGuideTutorial("SENSSAY"),
    },
  ];

  // ---------- 공지사항 (백엔드 연동) ----------
  const notices = ref([]);
  const noticeLoading = ref(false);

  const fetchNoticeList = async () => {
    noticeLoading.value = true;
    try {
      // 비로그인 상태에서도 조회 가능해야 하므로 토큰 유무에 따라 인스턴스 분기
      const apiInstance = getAccessToken() ? api : publicApi;
      const { data } = await apiInstance.post("/notice/NoticeListForMain", {});
      if (data?.success && data.result?.list) {
        notices.value = data.result.list.map((notice) => ({
          id: notice.noticeId,
          title: notice.title,
        }));
      } else {
        notices.value = [];
      }
    } catch (error) {
      console.error("Failed to load notice list:", error);
      notices.value = [];
    } finally {
      noticeLoading.value = false;
    }
  };

  // ---------- 검사·평가 일정 (백엔드 연동: tb_exam_display) ----------
  // admin '홈 화면 관리 > 시험 일정 관리'에서 등록한 내용을 그대로 게시한다.
  // 노출 순서/문구/기간은 전적으로 관리자가 정하므로 프론트는 정렬·가공을 하지 않는다.
  const examList = ref([]);
  const schedulePage = ref(0);
  const SCHEDULES_PER_PAGE = 3;
  const EXAM_TYPE_ORDER = ["MLSRD", "LITERACY", "ESSAY"];
  const EXAM_TYPE_THEME = { MLSRD: "violet", LITERACY: "blue", ESSAY: "teal" };

  const fetchExamSchedules = async () => {
    try {
      const apiInstance = getAccessToken() ? api : publicApi;
      const { data } = await apiInstance.post("/comm/getExamDisplayList", {});
      if (data?.success && data.result?.list) {
        examList.value = data.result.list;
      } else {
        examList.value = [];
      }
    } catch (error) {
      console.error("Failed to load exam schedules:", error);
      examList.value = [];
    }
  };

  // 서버가 to_char(…, 'YYYY-MM-DD')로 내려주므로 문자열 비교만으로 기간 판정이 가능하다
  // (Date 변환을 거치지 않아 타임존 영향이 없다).
  const todayText = () => {
    const now = new Date();
    const month = String(now.getMonth() + 1).padStart(2, "0");
    const day = String(now.getDate()).padStart(2, "0");
    return `${now.getFullYear()}-${month}-${day}`;
  };

  const formatDate = (value) => (value ? String(value).replaceAll("-", ".") : "");

  // 기간 표기: 시작~종료 (같은 해면 종료일은 MM.DD로 축약), 시작일 없으면 미정
  const periodText = (start, end) => {
    if (!start) return "미정";
    const startText = formatDate(start);
    if (!end) return `${startText} ~`;
    const endText =
      String(end).slice(0, 4) === String(start).slice(0, 4)
        ? formatDate(String(end).slice(5))
        : formatDate(end);
    return `${startText} ~ ${endText}`;
  };

  // 기간 3종은 [시작일+종료일] 또는 [직접입력 문구] 중 한쪽만 채워진다.
  // 문구가 있으면 문구를 그대로 게시한다.
  const rangeText = (text, start, end) => text || periodText(start, end);

  // 일정 진행 단계. status='auto'일 때 오늘 날짜로 계산하고,
  // 고정 status가 지정된 행은 아래 FIXED_STATUS_PHASE로 단계를 강제한다.
  // 직접입력 문구만 있고 날짜가 없는 기간은 판정에서 제외(다음 기간으로 넘어감)한다.
  const resolvePhase = (row) => {
    const today = todayText();

    // 접수기간과 응시기간이 겹치는 경우 응시기간이 우선이다
    // (이미 응시할 수 있는데 '검사 준비중'으로 막히지 않도록).
    if (
      row.exam_start_date &&
      row.exam_end_date &&
      today >= row.exam_start_date &&
      today <= row.exam_end_date
    ) {
      return "exam";
    }

    // 접수기간
    if (row.recept_start_date && today < row.recept_start_date) return "beforeReceipt";
    if (
      row.recept_start_date &&
      row.recept_end_date &&
      today <= row.recept_end_date
    ) {
      return "receipt";
    }

    // 접수 종료 ~ 응시 시작 사이는 '검사 준비중'
    // (응시기간 중인 경우는 위에서 이미 걸러졌다).
    if (row.exam_start_date && row.exam_end_date && today <= row.exam_end_date) {
      return "beforeExam";
    }

    // 성적발표기간 — 종료일은 선택값이며, 비어 있으면 기한 없이 계속 발표기간이다
    if (row.score_open_date) {
      if (today < row.score_open_date) return "afterExam";
      if (!row.score_open_end_date || today <= row.score_open_end_date) return "score";
      return "ended";
    }

    // 응시기간이 끝났는데 성적발표 날짜가 없으면 발표 준비중으로 둔다
    if (row.exam_end_date && today > row.exam_end_date) return "afterExam";

    // 판정할 날짜가 하나도 없는 경우(전부 직접입력 문구)는 준비중으로 둔다
    return "beforeExam";
  };

  // admin '홈 화면 관리 > 시험 일정 관리'의 상태 코드 → 단계.
  // 코드값이 단계명과 동일하지만, 화면 정의가 곧 매핑임을 드러내기 위해 명시해 둔다.
  // waiting/running/testEnd/end는 상태 코드 개편 이전 데이터 호환용.
  const FIXED_STATUS_PHASE = {
    beforeReceipt: "beforeReceipt",
    receipt: "receipt",
    exam: "exam",
    afterExam: "afterExam",
    score: "score",
    waiting: "beforeReceipt",
    running: "exam",
    testEnd: "afterExam",
    end: "ended",
  };

  // 단계 + 로그인/권한별 버튼. 비활성 버튼은 회색, 활성 버튼은 검사 유형별 색상이다.
  //
  //              | 비로그인      | 학생·교사     | 학교관리자
  //  접수기간전   | 접수 준비중   | 접수 준비중   | 접수 준비중      (비활성)
  //  접수기간     | 접수하기      | 검사 준비중   | 접수하기
  //  접수~응시사이 | 검사 준비중   | 검사 준비중   | 검사 준비중      (비활성)
  //  응시기간     | 응시하기      | 응시하기      | 응시하기
  //  응시기간종료 | 성적발표 준비중 | 성적발표 준비중 | 성적발표 준비중  (비활성)
  //  성적발표기간 | 결과 확인하기 | 결과 확인하기 | 결과 확인하기
  //
  //  - 접수는 학교관리자 업무이므로 학생·일반 교사에게는 '검사 준비중'(비활성)으로 노출한다.
  //  - 비로그인은 활성 버튼을 그대로 보여주고 클릭 시 안내 후 로그인으로 보낸다.
  const buildAction = (phase) => {
    const isSchoolAdmin =
      roleStore.tempRole === "teacher" && roleStore.isSchoolAdmin;

    switch (phase) {
      case "beforeReceipt":
        return { label: "접수 준비중", action: null };
      case "receipt":
        if (!isLoggedIn.value) return { label: "접수하기", action: "register" };
        return isSchoolAdmin
          ? { label: "접수하기", action: "register" }
          : { label: "검사 준비중", action: null };
      case "exam":
        return { label: "응시하기", action: "apply" };
      case "afterExam":
        return { label: "성적발표 준비중", action: null };
      case "score":
        return { label: "결과 확인하기", action: "result" };
      case "ended":
        return { label: "종료", action: null };
      case "beforeExam":
      default:
        return { label: "검사 준비중", action: null };
    }
  };

  const scheduleCards = computed(() =>
    examList.value.map((row) => {
      const phase = FIXED_STATUS_PHASE[row.status] ?? resolvePhase(row);
      const { label, action } = buildAction(phase);

      return {
        key: row.display_id,
        examType: row.exam_type,
        // 관리자가 textarea로 입력한 검사명 그대로 게시(개행은 CSS pre-line으로 유지)
        name: row.exam_name,
        apply: rangeText(row.recept_text, row.recept_start_date, row.recept_end_date),
        exam: rangeText(row.exam_text, row.exam_start_date, row.exam_end_date),
        result: rangeText(row.score_text, row.score_open_date, row.score_open_end_date),
        status: label,
        action,
        theme: EXAM_TYPE_THEME[row.exam_type] ?? "violet",
      };
    }),
  );

  const scheduleTotalPages = computed(() =>
    Math.max(1, Math.ceil(scheduleCards.value.length / SCHEDULES_PER_PAGE)),
  );

  const visibleSchedules = computed(() => {
    const start = schedulePage.value * SCHEDULES_PER_PAGE;
    return scheduleCards.value.slice(start, start + SCHEDULES_PER_PAGE);
  });

  // 접수하기: 나의 검사 관리 > 시험 계획(기존 진단검사 접수)으로
  const goRegisterExam = () => {
    router.push({ name: "MyTestTeacherRegisterExam" });
  };

  // 결과 확인하기: 나의 검사 관리 > 검사 결과로 (교사/학생 화면이 분리되어 있다)
  const goExamResult = () => {
    if (roleStore.tempRole === "teacher") {
      router.push({ name: "MyTestTeacherResult" });
    } else if (roleStore.tempRole === "student") {
      router.push({ name: "MyTestStudentResult" });
    } else {
      router.push({ name: "MyTestGate" });
    }
  };

  const handleScheduleAction = (row) => {
    if (!row.action) return;
    if (!isLoggedIn.value) {
      alert("로그인 후 이용해주세요.");
      goLogin();
      return;
    }
    // 교사 업무 화면은 시험별이므로 카드의 시험을 먼저 선택해 둔다
    if (roleStore.tempRole === "teacher" && row.examType) {
      useWorkspaceStore().setExamType(row.examType);
    }
    if (row.action === "register") {
      goRegisterExam();
      return;
    }
    if (row.action === "result") {
      goExamResult();
      return;
    }
    goApplyExam(row.examType);
  };

  const prevSchedulePage = () => {
    schedulePage.value =
      (schedulePage.value - 1 + scheduleTotalPages.value) %
      scheduleTotalPages.value;
  };
  const nextSchedulePage = () => {
    schedulePage.value = (schedulePage.value + 1) % scheduleTotalPages.value;
  };
</script>

<template>
  <div class="home-b">
    <!-- 히어로 슬라이더 -->
    <section class="hero">
      <button
        type="button"
        class="hero__arrow hero__arrow--prev"
        aria-label="이전 슬라이드"
        @click="onNavigate(activeSlide - 1)"
      >
        &#x2190;
      </button>
      <button
        type="button"
        class="hero__arrow hero__arrow--next"
        aria-label="다음 슬라이드"
        @click="onNavigate(activeSlide + 1)"
      >
        &#x2192;
      </button>

      <div class="hero__visual" aria-hidden="true">
        <img class="hero__visual-deco" :src="heroDecoImg" alt="" />
        <img class="hero__visual-std" :src="heroStdImg" alt="" />
      </div>
      <div class="container hero__content">
        <div class="hero__text">
          <h2 class="hero__title">{{ currentSlide.title }}</h2>
          <p class="hero__desc">{{ currentSlide.desc }}</p>
        </div>
      </div>

      <div class="hero__dots">
        <button
          v-for="(slide, idx) in heroSlides"
          :key="idx"
          type="button"
          class="hero__dot"
          :class="{ 'is-active': idx === activeSlide }"
          :aria-label="`${idx + 1}번 슬라이드`"
          @click="onNavigate(idx)"
        />
      </div>
    </section>

    <!-- 통합 안내 바 (히어로 하단 경계에 절반 걸침) -->
    <section class="container info-wrap">
      <div class="info-row">
        <!-- 좌: 고객센터 안내 -->
        <div class="info-bar">
          <div class="info-bar__brand">
            <span class="info-bar__icon" aria-hidden="true">
              <svg viewBox="0 0 32 32" fill="none">
                <path
                  d="M6 18v-2a10 10 0 0 1 20 0v2"
                  stroke="currentColor"
                  stroke-width="2.2"
                  stroke-linecap="round"
                />
                <rect x="3.5" y="17" width="6" height="9" rx="3" fill="currentColor" />
                <rect x="22.5" y="17" width="6" height="9" rx="3" fill="currentColor" />
              </svg>
            </span>
            <p class="info-bar__brand-text">고객센터 안내</p>
          </div>

          <div class="info-bar__cells">
            <div class="info-bar__cell">
              <p class="info-bar__label">운영 시간</p>
              <p class="info-bar__value">평일 09:00~18:00</p>
              <p class="info-bar__sub">(점심 12:00~13:00)</p>
            </div>
            <div class="info-bar__cell">
              <p class="info-bar__label">전화 문의</p>
              <p class="info-bar__value">070-4077-4425</p>
            </div>
            <div class="info-bar__cell">
              <p class="info-bar__label">온라인 문의</p>
              <button type="button" class="info-bar__link" @click="moveInquiry">
                <span class="info-bar__link-text">1:1 문의 바로가기</span>
                <span class="chevron">&#x203A;</span>
              </button>
            </div>
            <div class="info-bar__cell">
              <p class="info-bar__label">E-mail 문의</p>
              <p class="info-bar__value">whaleubt@nsdevil.com</p>
            </div>
          </div>
        </div>

        <!-- 우: 로그인 / 나의 검사 관리 -->
        <div class="info-cta">
          <template v-if="isLoggedIn">
            <div class="info-cta__full">
              <span class="info-cta__body">
                <span class="info-cta__label">{{ userInfo.name || "사용자" }} 님, 안녕하세요</span>
                <span class="info-cta__title info-cta__title--sm">
                  응시부터 결과까지<br />통합 관리하세요.
                </span>
              </span>
              <button type="button" class="info-cta__login" @click="goMyTest">
                나의 검사 관리
              </button>
            </div>
          </template>
          <template v-else>
            <div class="info-cta__full">
              <span class="info-cta__body">
                <span class="info-cta__label">SEN스쿨 아이디로</span>
                <span class="info-cta__title info-cta__title--sm">
                  다양한 평가와 검사를<br />통합 관리하세요.
                </span>
              </span>
              <button type="button" class="info-cta__login" @click="goLogin">
                로그인
              </button>
            </div>
          </template>
        </div>
      </div>
    </section>

    <!-- 서비스 카드 -->
    <section class="container services">
      <article
        v-for="card in serviceCards"
        :key="card.label"
        class="service-card"
        :class="`service-card--${card.theme}`"
      >
        <div class="service-card__head">
          <span class="service-card__label">{{ card.label }}</span>
        </div>
        <div class="service-card__body">
          <div class="service-card__info">
            <h3 class="service-card__title">{{ card.title }}</h3>
            <div class="service-card__actions">
              <button
                type="button"
                class="service-card__cta"
                @click="card.onClick()"
              >
                {{ card.cta }}
              </button>
              <!-- 응시하기는 로그인 상태에서만 노출 -->
              <button
                v-if="isLoggedIn"
                type="button"
                class="service-card__cta service-card__cta--apply"
                @click="goApplyExam(card.examType)"
              >
                응시하기
              </button>
            </div>
          </div>
          <img class="service-card__illust" :src="card.img" alt="" aria-hidden="true" />
        </div>
      </article>
    </section>

    <!-- 공지사항 / 시험 일정 -->
    <section class="container boards">
      <article class="board-card board-card--notice">
        <div class="board-card__head">
          <h3>공지사항</h3>
          <button type="button" class="board-card__more" @click="moveNoticeList">
            더보기 <span class="chevron">&#x2192;</span>
          </button>
        </div>
        <ul v-if="notices.length > 0" class="notice-list">
          <li v-for="notice in notices" :key="notice.id">
            <span class="notice-list__tag">공지</span>
            <button
              type="button"
              class="notice-list__title"
              @click="moveNoticeDetail(notice.id)"
            >
              {{ notice.title }}
            </button>
            <svg
              class="notice-list__arrow"
              xmlns="http://www.w3.org/2000/svg"
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
              aria-hidden="true"
            >
              <path d="m9 18 6-6-6-6" />
            </svg>
          </li>
        </ul>
        <div v-else-if="!noticeLoading" class="notice-empty">
          공지사항이 없습니다.
        </div>
      </article>

      <article class="board-card board-card--schedule">
        <div class="board-card__head">
          <h3>검사·평가 일정</h3>
          <div class="board-card__nav">
            <span class="board-card__page">
              {{ schedulePage + 1 }} / {{ scheduleTotalPages }}
            </span>
            <button type="button" aria-label="이전" @click="prevSchedulePage">
              &#x2039;
            </button>
            <button type="button" aria-label="다음" @click="nextSchedulePage">
              &#x203A;
            </button>
          </div>
        </div>
        <div v-if="visibleSchedules.length > 0" class="schedule-cards">
          <div
            v-for="row in visibleSchedules"
            :key="row.key"
            class="schedule-card"
            :class="`schedule-card--${row.theme}`"
          >
            <p class="schedule-card__name">{{ row.name }}</p>
            <dl class="schedule-card__meta">
              <div><dt>접수기간</dt><dd>{{ row.apply }}</dd></div>
              <div><dt>시험기간</dt><dd>{{ row.exam }}</dd></div>
              <div><dt>성적발표</dt><dd>{{ row.result }}</dd></div>
            </dl>
            <button
              type="button"
              class="schedule-card__status"
              :class="{ 'schedule-card__status--disabled': !row.action }"
              :disabled="!row.action"
              @click="handleScheduleAction(row)"
            >
              {{ row.status }}
            </button>
          </div>
        </div>
        <div v-else class="notice-empty">등록된 시험 일정이 없습니다.</div>
      </article>
    </section>

    <NoticeModal v-model:show="showNoticeModal" :notice-info="noticeInfo" />
    <SurveyPopupModal
      v-model:show="showSurveyPopup"
      :surveys="surveyPopupList"
      @join="joinSurvey"
      @hide-today="hideSurveyPopupForToday"
    />
  </div>

  <!-- 개인정보 수집·이용 동의 -->
  <PrivacyTeaModal
    v-model:show="isOpenPrivacyTeaModal"
    @cancel="handlePrivacyCancel"
  />
  <!--  <PrivacyStuModal
    v-model:show="isOpenPrivacyStuModal"
    @cancel="handlePrivacyCancel"
  />-->
  <NewPrivacyModal v-model:show="newPrivacyModal" />
</template>

<style lang="scss" scoped>
  .home-b {
    background: #f5f6fb;
    color: #1f2333;
    letter-spacing: -0.02em;
    /* 콘텐츠가 짧아도 배경이 푸터까지 이어지도록 main 영역을 가득 채운다 */
    min-height: calc(100dvh - var(--smoe-header-h) - var(--smoe-footer-h));

    :where(button) {
      background: none;
      border: none;
      cursor: pointer;
      font: inherit;
      color: inherit;
      padding: 0;
    }
  }

  .container {
    width: 100%;
    max-width: 1280px;
    margin: 0 auto;
    padding: 0 24px;
  }

  .chevron {
    font-size: 1em;
    line-height: 1;
  }

  /* ---------- 히어로 ---------- */
  .hero {
    position: relative;
    background: linear-gradient(115deg, #e9f2ff 0%, #eef1ff 45%, #e7fbf4 100%);
    padding: 40px 0 50px;
    overflow: hidden;

    &__content {
      position: relative;
      z-index: 1;
      display: flex;
      align-items: center;
      min-height: 264px;
    }

    &__text {
      flex: 1;
      max-width: 600px;
      padding-left: 40px;
    }

    &__title {
      font-size: 40px;
      line-height: 1.24;
      font-weight: 800;
      margin-bottom: 24px;
      background: linear-gradient(90deg, #fc62ff 0%, #0c83fb 46.63%, #15d4cb 100%);
      background-clip: text;
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
      white-space: pre-line;
    }

    &__desc {
      font-size: 16px;
      line-height: 1.75;
      color: #545a6e;
      white-space: pre-line;
    }

    /* 시안 기준: 장식(차트)과 학생 레이어를 개별 배치, 하단은 히어로 밖으로 잘림 */
    &__visual {
      position: absolute;
      inset: 0;
      z-index: 0;
      line-height: 0;

      img {
        position: absolute;
        height: auto;
      }
    }

    &__visual-deco {
      top: 30px;
      left: calc(50% + 40px);
      width: 590px;
      opacity: 0.7;
    }

    &__visual-std {
      top: 100px;
      left: calc(50% + 40px);
      width: 560px;
    }

    &__arrow {
      position: absolute;
      top: 42%;
      width: 46px;
      height: 46px;
      background: #fff;
      border-radius: 50%;
      color: #6b7085;
      font-size: 20px;
      line-height: 1;
      display: flex;
      align-items: center;
      justify-content: center;
      box-shadow: 0 4px 16px rgba(31, 35, 51, 0.12);
      transition: color 0.2s;
      z-index: 2;

      &:hover {
        color: #4f46e5;
      }

      &--prev {
        left: max(24px, calc(50% - 700px));
      }
      &--next {
        right: max(24px, calc(50% - 700px));
      }
    }

    &__dots {
      position: absolute;
      left: 50%;
      bottom: 58px;
      transform: translateX(-50%);
      display: flex;
      align-items: center;
      gap: 7px;
      z-index: 2;
      background: rgba(255, 255, 255, 0.9);
      border-radius: 999px;
      padding: 8px 14px;
      box-shadow: 0 2px 10px rgba(31, 35, 51, 0.08);
    }

    &__dot {
      width: 8px;
      height: 8px;
      border-radius: 999px;
      background: #aab1c4;
      transition: all 0.2s;

      &.is-active {
        width: 26px;
        background: #2563eb;
      }
    }
  }

  /* ---------- 통합 안내 바 ---------- */
  .info-wrap {
    position: relative;
    z-index: 3;
  }

  /* 좌: 고객센터 안내 / 우: 로그인·나의 검사 관리 */
  .info-row {
    display: flex;
    align-items: stretch;
    gap: 22px;
    margin-top: -50px;
  }

  .info-bar {
    flex: 1;
    min-width: 0;
    display: flex;
    align-items: center;
    background: linear-gradient(
      97.34deg,
      #9747ff -14.69%,
      #0a7cff 49.98%,
      #26ddcc 114.66%
    );
    color: #fff;
    border-radius: 18px;
    box-shadow: 0 18px 40px rgba(79, 70, 229, 0.26);
    padding: 22px 26px;

    &__brand {
      flex-shrink: 0;
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 10px;
      padding: 0 26px 0 6px;
    }

    &__icon {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 52px;
      height: 52px;
      border-radius: 50%;
      background: rgba(255, 255, 255, 0.18);
      color: #fff;

      svg {
        width: 28px;
        height: 28px;
      }
    }

    &__brand-text {
      font-size: 14px;
      font-weight: 700;
      white-space: nowrap;
    }

    &__cells {
      flex: 1;
      min-width: 0;
      display: flex;
      align-items: center;
    }

    &__cell {
      flex: 1;
      min-width: 0;
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      justify-content: center;
      gap: 6px;
      padding: 0 22px;

      & + & {
        border-left: 1px solid rgba(255, 255, 255, 0.28);
      }
    }

    &__label {
      font-size: 13px;
      color: rgba(255, 255, 255, 0.82);
      white-space: nowrap;
    }

    &__value {
      font-size: 14px;
      font-weight: 500;
      white-space: nowrap;
    }

    &__sub {
      /* '평일 09:00~18:00'와의 간격을 좁힌다 (셀 기본 gap 6px 상쇄) */
      margin-top: -8px;
      font-size: 12.5px;
      color: rgba(255, 255, 255, 0.8);
      white-space: nowrap;
    }

    &__link {
      font-size: 14px;
      font-weight: 700;
      display: inline-flex;
      align-items: center;
      gap: 5px;
      color: #fff;
      white-space: nowrap;
    }

    &__link-text {
      text-underline-offset: 4px;
    }
  }

  .info-cta {
    flex: 0 0 344px;
    display: flex;
    flex-direction: column;
    justify-content: center;
    background: #0a7cff;
    color: #fff;
    border-radius: 18px;
    box-shadow: 0 18px 40px rgba(10, 124, 255, 0.26);
    padding: 24px 26px;

    /* 텍스트(좌) + 버튼·화살표(우) 가로 배치 */
    &__full {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 14px;
      width: 100%;
      height: 100%;
      text-align: left;
      color: inherit;
    }

    &__body {
      display: flex;
      flex-direction: column;
      gap: 7px;
      min-width: 0;
    }

    &__label {
      font-size: 13px;
      color: rgba(255, 255, 255, 0.86);
    }

    /* 안내 문구 (로그인 전·후 공용) */
    &__title {
      font-size: 19px;
      font-weight: 800;
      line-height: 1.4;

      &--sm {
        font-size: 14px;
      }
    }

    /* 흰색 버튼 (우측) — 로그인 전 [로그인] / 로그인 후 [나의 검사 관리] */
    &__login {
      flex-shrink: 0;
      padding: 11px 22px;
      border-radius: 10px;
      background: #fff;
      color: #1f2333;
      font-size: 15px;
      font-weight: 700;
      transition: filter 0.2s;

      &:hover {
        filter: brightness(0.96);
      }
    }
  }

  .services {
    display: flex;
    gap: 24px;
    margin-top: 42px;
  }

  .service-card {
    flex: 1;
    background: #fff;
    border-radius: 20px;
    box-shadow: 0 10px 30px rgba(31, 35, 51, 0.06);
    padding: 26px 26px 28px;
    display: flex;
    flex-direction: column;
    transition:
      transform 0.2s,
      box-shadow 0.2s;

    &:hover {
      transform: translateY(-4px);
      box-shadow: 0 16px 38px rgba(31, 35, 51, 0.12);
    }

    &__head {
      display: flex;
      align-items: center;
      margin-bottom: 16px;
    }

    &__label {
      font-size: 15px;
      font-weight: 800;
    }

    &__body {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 10px;
      flex: 1;
    }

    &__info {
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      flex: 1;
      min-width: 0;
    }

    &__title {
      font-size: 18px;
      font-weight: 600;
      line-height: 1.45;
      white-space: pre-line;
      margin-bottom: 20px;
    }

    &__actions {
      display: flex;
      align-items: center;
      gap: 8px;
      flex-wrap: wrap;
    }

    &__cta {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      min-width: 100px;
      border-radius: 999px;
      padding: 10px 24px;
      font-size: 14px;
      font-weight: 700;
      transition: filter 0.2s;

      &:hover {
        filter: brightness(0.97);
      }
    }

    &__illust {
      width: 116px;
      height: auto;
      flex-shrink: 0;
    }

    /* 테마별 색상 */
    &--violet {
      .service-card__label {
        color: #7c3aed;
      }
      .service-card__cta {
        background: #f1ecfe;
        color: #6d28d9;

        &--apply {
          background: #9747ff;
          color: #fff;
        }
      }
    }

    &--blue {
      .service-card__label {
        color: #2563eb;
      }
      .service-card__cta {
        background: #e8f1ff;
        color: #2563eb;

        &--apply {
          background: #0a7cff;
          color: #fff;
        }
      }
    }

    &--teal {
      .service-card__label {
        color: #0d9488;
      }
      .service-card__cta {
        background: #e3f7f2;
        color: #0d9488;

        &--apply {
          background: #0ba5a0;
          color: #fff;
        }
      }
    }
  }

  /* ---------- 공지사항 / 시험 일정 ---------- */
  .boards {
    display: flex;
    gap: 24px;
    margin-top: 24px;
    padding-bottom: 56px;
  }

  .board-card {
    background: #fff;
    border-radius: 20px;
    box-shadow: 0 10px 30px rgba(31, 35, 51, 0.06);
    padding: 26px 28px;

    &--notice {
      /* 위 서비스 카드(3등분) 1개와 동일 폭으로 정렬 */
      flex: 0 0 calc((100% - 48px) / 3);
      min-width: 0;
      display: flex;
      flex-direction: column;
    }

    &--schedule {
      flex: 1;
      min-width: 0;
      background: #f4f7fe;

      .board-card__head h3 {
        color: #2563eb;
      }
    }

    &__head {
      display: flex;
      align-items: center;
      justify-content: space-between;
      margin-bottom: 14px;

      h3 {
        font-size: 18px;
        font-weight: 600;
        color: #1f2333;
      }
    }

    &__more {
      display: inline-flex;
      align-items: center;
      gap: 4px;
      font-size: 13px;
      color: #9aa0b3;

      &:hover {
        color: #4f46e5;
      }
    }

    &__nav {
      display: flex;
      align-items: center;
      gap: 8px;

      button {
        width: 30px;
        height: 30px;
        border: 1px solid #e6e8f0;
        border-radius: 50%;
        background: #fff;
        color: #8a8fa3;
        font-size: 15px;
        line-height: 1;

        &:hover {
          color: #545a6e;
        }
      }
    }

    &__page {
      background: #fff;
      border-radius: 999px;
      padding: 6px 14px;
      font-size: 13px;
      color: #6b7085;
      margin-right: 2px;
    }
  }

  .notice-list {
    flex: 1;
    display: flex;
    flex-direction: column;

    li {
      flex: 1;
      display: flex;
      align-items: center;
      gap: 12px;
      padding: 0 0;
      border-bottom: 1px solid #eef0f6;

      &:last-child {
        border-bottom: none;
      }
    }

    &__tag {
      flex-shrink: 0;
      padding: 3px 10px;
      border-radius: 6px;
      background: #eef1fe;
      color: #4f6bf6;
      font-size: 12px;
      font-weight: 700;
    }

    &__title {
      flex: 1;
      min-width: 0;
      font-size: 14px;
      text-align: left;
      color: #3a3f52;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;

      &:hover {
        color: #4f46e5;
        text-decoration: underline;
      }
    }

    &__arrow {
      color: #c3c8d6;
      flex-shrink: 0;
    }
  }

  .notice-empty {
    padding: 40px 0;
    text-align: center;
    font-size: 13px;
    color: #9aa0b3;
  }

  .schedule-cards {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 15px;
  }

  .schedule-card {
    /* grid 3열 고정 — 마지막 페이지에 카드가 1~2개여도 3등분 크기 유지 */
    min-width: 0;
    border-radius: 12px;
    padding: 20px 20px 22px;
    background: #fff;
    border: 1px solid #e6e8f0;
    display: flex;
    flex-direction: column;

    &__name {
      font-size: 15px;
      font-weight: 600;
      line-height: 1.45;
      white-space: pre-line;
      min-height: 44px;
      margin-bottom: 18px;
    }

    &__meta {
      display: flex;
      flex-direction: column;
      gap: 9px;
      margin-bottom: 20px;

      div {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 8px;
      }

      dt {
        font-size: 12px;
        font-weight: 600;
        padding: 3px 9px;
        border-radius: 6px;
      }

      dd {
        font-size: 13px;
        font-weight: 500;
        color: #545a6e;
        text-align: right; // 성적발표처럼 두 줄로 넘어가는 값도 오른쪽에 맞춘다
      }
    }

    &__status {
      margin-top: auto;
      width: 100%;
      height: 42px;
      border-radius: 999px;
      font-size: 14px;
      font-weight: 700;

      /* 준비중/종료 등 클릭 불가 상태만 회색, 접수/응시/결과는 검사 유형별 색상(아래) */
      &--disabled {
        background: #e9ebf0;
        color: #8990a3;
        cursor: default;
      }
    }

    /* 검사 유형별 색상 — 상태 버튼은 서비스 카드 '응시하기'와 동일한 채움 스타일 */
    &--violet {
      .schedule-card__meta dt {
        background: #f3edfe;
        color: #9747ff;
      }
      .schedule-card__status:not(.schedule-card__status--disabled) {
        background: #9747ff;
        color: #fff;
      }
    }
    &--blue {
      .schedule-card__meta dt {
        background: #e8f1ff;
        color: #0a7cff;
      }
      .schedule-card__status:not(.schedule-card__status--disabled) {
        background: #0a7cff;
        color: #fff;
      }
    }
    &--teal {
      .schedule-card__meta dt {
        background: #e3f7f2;
        color: #0ba5a0;
      }
      .schedule-card__status:not(.schedule-card__status--disabled) {
        background: #0ba5a0;
        color: #fff;
      }
    }
  }
</style>
